'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faRegistered } from '@fortawesome/pro-regular-svg-icons';
import { Chip } from '../Chip';
import { Tooltip } from '../Tooltip';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Typography } from '../Typography';
import { useGetCodeByNameQuery } from 'src/api/redux';

enum CodeType {
  CPT = 'cpt',
  HCPCS = 'hcpcs',
  DRUG = 'drug',
  DEFAULT = 'default',
}

type CodeStyleType = {
  readonly backgroundColor: string;
  readonly decoratorColor: string;
  readonly border: string;
};

const CODE_STYLES: Record<CodeType, CodeStyleType> = {
  [CodeType.CPT]: {
    backgroundColor: '!bg-sky-200',
    decoratorColor: '!bg-sky-600',
    border: '!border !border-sky-600',
  },
  [CodeType.HCPCS]: {
    backgroundColor: '!bg-magenta-100',
    decoratorColor: '!bg-magenta-400',
    border: '!border !border-magenta-400',
  },
  [CodeType.DRUG]: {
    backgroundColor: '!bg-orange-100',
    decoratorColor: '!bg-orange-400',
    border: '!border !border-orange-400',
  },
  [CodeType.DEFAULT]: {
    backgroundColor: '!bg-indigo-100',
    decoratorColor: '!bg-indigo-400',
    border: '!border !border-indigo-400',
  },
} as const;

const parseCode = (codeValue: string | null | undefined): ParsedCode => {
  if (!codeValue || typeof codeValue !== 'string') {
    return { prefix: '', suffix: '', isValid: false };
  }

  const code = codeValue.trim();
  if (!code) {
    return { prefix: '', suffix: '', isValid: false };
  }

  const hyphenIndex = code.indexOf('-');

  if (hyphenIndex === -1) {
    return {
      prefix: code,
      suffix: '',
      isValid: true,
    };
  }

  return {
    prefix: code.substring(0, hyphenIndex),
    suffix: code.substring(hyphenIndex),
    isValid: true,
  };
};

const getCodeType = (prefix: string): CodeType => {
  const lowerPrefix = prefix.toLowerCase();

  if (lowerPrefix.startsWith('cpt')) return CodeType.CPT;
  if (lowerPrefix.startsWith('hcpcs')) return CodeType.HCPCS;
  if (lowerPrefix.startsWith('drug')) return CodeType.DRUG;

  return CodeType.DEFAULT;
};

/**
 * Represents a medical code object
 */
interface CodeData {
  id: string;
  code_name?: string;
  category?: string;
  description?: string;
  sub_categories?: string[];
}

interface ParsedCode {
  prefix: string;
  suffix: string;
  isValid: boolean;
}

interface CodeChipProps {
  code: string;
  modifier?: string;
  onDelete?: () => void;
  onModifierClick?: () => void;
  onModifierDelete?: () => void;
  showModifierAffordance?: boolean;
  'data-testid'?: string;
}

const CodeTooltipContent = ({ codeData }: { codeData?: CodeData }) => {
  if (!codeData) {
    return (
      <Typography className="text-xs text-neutral-600 leading-tight">
        Loading code information...
      </Typography>
    );
  }

  return (
    <div className="flex flex-col gap-1 max-h-32 overflow-hidden overflow-y-auto">
      {codeData.category && (
        <Typography className="text-sm leading-tight">
          {codeData.category}
        </Typography>
      )}
      {codeData.sub_categories && codeData.sub_categories.length > 0 && (
        <Typography className="text-xs text-neutral-600 leading-tight">
          {codeData.sub_categories.join(', ')}
        </Typography>
      )}
      <Typography className="text-xs text-neutral-600 leading-tight">
        {codeData.description || 'No description available.'}
      </Typography>
    </div>
  );
};

const CodeChip = ({
  code,
  modifier,
  onDelete,
  onModifierClick,
  onModifierDelete,
  showModifierAffordance = false,
  'data-testid': testId,
}: CodeChipProps) => {
  const {
    data: codeData,
    error,
    isLoading,
  } = useGetCodeByNameQuery(
    { codeIds: [code] },
    { skip: !code }
  );

  const cd: CodeData | undefined = codeData?.results?.[0] as CodeData | undefined;
  const parsedCode = parseCode(cd?.id || cd?.code_name || code);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  // Show loading state
  if (isLoading) {
    return (
      <Chip
        size="sm"
        className={`${CODE_STYLES[CodeType.DEFAULT].backgroundColor} ${
          CODE_STYLES[CodeType.DEFAULT].border
        } flex items-center h-6 pl-0 opacity-50`}
        data-testid={testId}
      >
        <div className="px-3">
          <p className="text-primary font-normal text-sm">Loading...</p>
        </div>
      </Chip>
    );
  }

  // Show error state
  if (error) {
    return (
      <Chip
        size="sm"
        className={`${CODE_STYLES[CodeType.DEFAULT].backgroundColor} ${
          CODE_STYLES[CodeType.DEFAULT].border
        } flex items-center h-6 pl-0`}
        data-testid={testId}
      >
        <div className="px-3">
          <p className="text-primary font-normal text-sm">{code}</p>
        </div>
      </Chip>
    );
  }

  // Handle invalid parsing
  if (!parsedCode.isValid) {
    return (
      <Chip
        size="sm"
        className={`${CODE_STYLES[CodeType.DEFAULT].backgroundColor} ${
          CODE_STYLES[CodeType.DEFAULT].border
        } flex items-center h-6 pl-0`}
        data-testid={testId}
      >
        <div className="px-3">
          <p className="text-primary font-normal text-sm">
            {code || 'Invalid Code'}
          </p>
        </div>
      </Chip>
    );
  }

  const { prefix, suffix } = parsedCode;
  const codeType = getCodeType(prefix);
  const styles = CODE_STYLES[codeType];
  const showRegistered = codeType === CodeType.CPT;

  return (
    <Chip
      size="sm"
      className={`${styles.backgroundColor} ${styles.border} flex items-center h-6 pl-0 [&>.truncate]:overflow-visible`}
      startDecorator={
        <div
          className={`${styles.decoratorColor} w-2 h-6 rounded-l-full pl-3`}
        />
      }
      data-testid={testId}
    >
      <Tooltip
        slotProps={{
          containerClassName: 'bg-white border border-neutral-200',
          contentClassName: 'min-w-48 max-w-96',
        }}
        content={<CodeTooltipContent codeData={cd} />}
        position="top"
      >
        <div className="flex items-center justify-between w-full">
          <div className={`flex items-center ${onDelete ? '' : 'pr-3'}`}>
            <p className="text-primary font-normal text-sm">{prefix}</p>
            {showRegistered && (
              <Icon
                icon={faRegistered}
                style={{ fontSize: '0.5rem', marginBottom: '0.2rem' }}
                color="#262626"
              />
            )}
            <p className="text-primary font-normal text-sm">{suffix}</p>
          </div>
          {modifier && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onModifierClick?.();
              }}
              className="bg-amber-100 text-amber-700 border border-amber-500 rounded-full px-2 text-xs leading-4 ml-1 hover:bg-amber-200 cursor-pointer"
              data-testid={`code-chip-modifier-${modifier}`}
              aria-label={`Change modifier ${modifier}`}
            >
              {modifier}
            </button>
          )}
          {!modifier && showModifierAffordance && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onModifierClick?.();
              }}
              className="text-neutral-400 text-xs ml-1 hover:text-neutral-600"
              data-testid="code-chip-add-modifier"
              aria-label="Add modifier"
            >
              + modifier
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              className="text-neutral-700"
              data-testid={`code-chip-delete-${
                cd?.id || cd?.code_name || code
              }`}
            >
              <Icon icon={faXmark} className="w-4 h-4" color="#262626" />
            </button>
          )}
        </div>
      </Tooltip>
    </Chip>
  );
};

export default CodeChip;
